/**
 * frontend/src/pages/valuation/_IntakeParticulars.tsx
 *
 * VAL-ENGAGEMENT-PARTICULARS-1 / VAL-PARTICULARS-UI-1 — the ten VS Section
 * 100 engagement particulars, collected on Intake and persisted onto the
 * ENGAGEMENT itself (not the run body — these describe the engagement, not
 * one calculation of it). Split out of ValuationIntake.tsx under
 * docs/FILE_SIZE_POLICY.md ahead of landing, not after, to keep that screen
 * inside the 400-line stop.
 *
 * Standard of value drops `liquidation_value` (owner ruling 2026-09-15),
 * leaving fair market / fair / investment value.
 *
 * VAL-WIRE-CONSISTENCY-1 R2: premise of value is the engagement's own value,
 * chosen on the create form — shown here as an editable field and never
 * overwritten. `going_concern` is the default only when none was chosen.
 *
 * No client-side block: leaving a required field blank never prevents a
 * run. Only finalize refuses — see core/valuation/finalize_particulars.py.
 *
 * VAL-PATH-COMPANY (P3): I1-I9 are edited on step 1 (ValuationCompany.tsx)
 * and saved in the same PATCH as the company profile. The two declarations
 * I10-I11 are on the signer's Sign step (VAL-PATH-P7c, _SignDeclarations),
 * which renders IntakeDeclarationsSection.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { ValuationEngagement } from '@/types/valuation'
import { HelpDot } from '@/components/ui/help-dot'
import { HELP_TEXTS } from './_helpTexts'

export interface ParticularsState {
  prepared_for: string
  premise_of_value: string
  effective_date: string
  purpose: string
  intended_users: string
  standard_of_value: string
  subject_interest: string
  interest_characteristics: string
  sources_relied_upon: string
}

export const BLANK_PARTICULARS: ParticularsState = {
  prepared_for: '',
  premise_of_value: 'going_concern',
  effective_date: '',
  purpose: '',
  intended_users: '',
  standard_of_value: '',
  subject_interest: '',
  interest_characteristics: '',
  sources_relied_upon: '',
}

/** Hydrate from whatever the engagement already carries — null becomes ''. */
export function particularsFromEngagement(en: ValuationEngagement): ParticularsState {
  return {
    prepared_for: en.prepared_for ?? '',
    premise_of_value: en.premise_of_value?.trim() || 'going_concern',
    effective_date: en.effective_date ?? '',
    purpose: en.purpose ?? '',
    intended_users: en.intended_users ?? '',
    standard_of_value: en.standard_of_value ?? '',
    subject_interest: en.subject_interest ?? '',
    interest_characteristics: en.interest_characteristics ?? '',
    sources_relied_upon: en.sources_relied_upon ?? '',
  }
}

/** The PATCH shape — a blank date or premise is sent as null, never ''. */
export function particularsToPayload(p: ParticularsState) {
  return {
    ...p,
    effective_date: p.effective_date || null,
    premise_of_value: p.premise_of_value.trim() || null,
  }
}

export interface DeclarationsState {
  analyst_has_financial_interest: boolean
  fee_is_contingent: boolean
}

export const BLANK_DECLARATIONS: DeclarationsState = {
  analyst_has_financial_interest: false,
  fee_is_contingent: false,
}

export function declarationsFromEngagement(en: ValuationEngagement): DeclarationsState {
  return {
    analyst_has_financial_interest: en.analyst_has_financial_interest,
    fee_is_contingent: en.fee_is_contingent,
  }
}

// Owner ruling 2026-09-15: liquidation_value dropped this phase.
export const STANDARDS_OF_VALUE = [
  { value: 'fair_market_value', label: 'Fair market value' },
  { value: 'fair_value', label: 'Fair value' },
  { value: 'investment_value', label: 'Investment value' },
]

export function IntakeParticularsSection({
  particulars,
  setParticulars,
}: {
  particulars: ParticularsState
  setParticulars: (next: ParticularsState) => void
}) {
  function update(patch: Partial<ParticularsState>) {
    setParticulars({ ...particulars, ...patch })
  }

  return (
    <Card id="particulars">
      <CardHeader><CardTitle>Engagement particulars</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-pal-ink-muted">
          Required fields are needed to finalize — leaving one blank never blocks running a
          valuation.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <div className="flex items-center gap-1"><Label htmlFor="prepared_for" className="text-xs">Prepared for</Label><HelpDot label="Prepared for" paragraphs={HELP_TEXTS['prepared_for']} testId="info-prepared_for" /></div>
            <Input
              id="prepared_for"
              value={particulars.prepared_for}
              onChange={e => update({ prepared_for: e.target.value })}
            />
          </div>
          <div>
            <div className="flex items-center gap-1"><Label htmlFor="effective_date" className="text-xs">Valuation date (effective date)</Label><HelpDot label="Valuation date (effective date)" paragraphs={HELP_TEXTS['effective_date']} testId="info-effective_date" /></div>
            <Input
              id="effective_date"
              type="date"
              value={particulars.effective_date}
              onChange={e => update({ effective_date: e.target.value })}
            />
          </div>
          <div>
            <div className="flex items-center gap-1"><Label htmlFor="standard_of_value" className="text-xs">Standard of value *</Label><HelpDot label="Standard of value" paragraphs={HELP_TEXTS['standard_of_value']} testId="info-standard_of_value" /></div>
            <select
              id="standard_of_value"
              className="h-9 w-full rounded-md border border-pal-line-strong bg-pal-white px-2 text-sm"
              value={particulars.standard_of_value}
              onChange={e => update({ standard_of_value: e.target.value })}
            >
              <option value="">Select…</option>
              {STANDARDS_OF_VALUE.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1"><Label htmlFor="premise_of_value" className="text-xs">Premise of value *</Label><HelpDot label="Premise of value" paragraphs={HELP_TEXTS['premise_of_value']} testId="info-premise_of_value" /></div>
          <Input
            id="premise_of_value"
            value={particulars.premise_of_value}
            onChange={e => update({ premise_of_value: e.target.value })}
          />
          <p className="mt-0.5 text-xs text-pal-ink-faint">
            As chosen when the engagement was created; going concern if none was chosen.
          </p>
        </div>

        <div>
          <div className="flex items-center gap-1"><Label htmlFor="purpose" className="text-xs">Purpose of the engagement *</Label><HelpDot label="Purpose of the engagement" paragraphs={HELP_TEXTS['purpose']} testId="info-purpose" /></div>
          <Textarea
            id="purpose" rows={2}
            value={particulars.purpose}
            onChange={e => update({ purpose: e.target.value })}
          />
        </div>

        <div>
          <div className="flex items-center gap-1"><Label htmlFor="intended_users" className="text-xs">Intended users *</Label><HelpDot label="Intended users" paragraphs={HELP_TEXTS['intended_users']} testId="info-intended_users" /></div>
          <Textarea
            id="intended_users" rows={2}
            value={particulars.intended_users}
            onChange={e => update({ intended_users: e.target.value })}
          />
        </div>

        <div>
          <div className="flex items-center gap-1"><Label htmlFor="subject_interest" className="text-xs">Subject interest *</Label><HelpDot label="Subject interest" paragraphs={HELP_TEXTS['subject_interest']} testId="info-subject_interest" /></div>
          <Input
            id="subject_interest"
            value={particulars.subject_interest}
            onChange={e => update({ subject_interest: e.target.value })}
            placeholder="e.g. 100% of the membership interests"
          />
        </div>

        <div>
          <div className="flex items-center gap-1"><Label htmlFor="interest_characteristics" className="text-xs">Interest characteristics</Label><HelpDot label="Interest characteristics" paragraphs={HELP_TEXTS['interest_characteristics']} testId="info-interest_characteristics" /></div>
          <Textarea
            id="interest_characteristics" rows={2}
            value={particulars.interest_characteristics}
            onChange={e => update({ interest_characteristics: e.target.value })}
            placeholder="Control vs. minority, marketable vs. non-marketable"
          />
        </div>

        <div>
          <div className="flex items-center gap-1"><Label htmlFor="sources_relied_upon" className="text-xs">Sources relied upon</Label><HelpDot label="Sources relied upon" paragraphs={HELP_TEXTS['sources_relied_upon']} testId="info-sources_relied_upon" /></div>
          <Textarea
            id="sources_relied_upon" rows={2}
            value={particulars.sources_relied_upon}
            onChange={e => update({ sources_relied_upon: e.target.value })}
          />
        </div>
      </CardContent>
    </Card>
  )
}

// I10-I11 — same two checkboxes and strings as before P3, now on their own.
export function IntakeDeclarationsSection({
  declarations,
  setDeclarations,
}: {
  declarations: DeclarationsState
  setDeclarations: (next: DeclarationsState) => void
}) {
  return (
    <Card>
      <CardContent className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-pal-ink">
          <input
            type="checkbox"
            checked={declarations.analyst_has_financial_interest}
            onChange={e => setDeclarations({ ...declarations, analyst_has_financial_interest: e.target.checked })}
          />
          Analyst has a financial interest
        </label>
        <label className="flex items-center gap-2 text-sm text-pal-ink">
          <input
            type="checkbox"
            checked={declarations.fee_is_contingent}
            onChange={e => setDeclarations({ ...declarations, fee_is_contingent: e.target.checked })}
          />
          Fee is contingent
        </label>
      </CardContent>
    </Card>
  )
}
